import Cookies from "js-cookie";
import { v4 as uuid } from "uuid";

import {
  getLocalStorageItem,
  localStorageSafe,
  sessionStorageSafe,
  setCookie,
  setLocalStorageItem
} from "../storage/storage";
import {
  STORAGE_KEY_TOKEN,
  STORAGE_KEY_CONTENT_CACHE_LAST_CHECK, STORAGE_KEY_CONTENT_VERSION,
  STORAGE_KEY_REFRESH_COUNT, _deprecated_STORAGE_KEY_REFRESH_TOKEN
} from "../storage/storage.constants";
import {COOKIE_KEY_DONATE_CLOSED_COUNT, COOKIE_KEY_DONATE_NEXT_SHOW} from "../donate";
import {COOKIE_KEY_THEME} from "../theme/theme";
import { STORAGE_KEY_IS_PWA_INSTALLED } from "../../hooks/pwa/pwa.constants";
import { consoleLogInfo } from "../console";
import { getServerToken } from "../url/url";
import { SETTING } from "../settings/constants";
import { STORAGE_KEY_LAST_SC_ACTIONS } from "../studyCenter/constants";
import { TUTORIALS_LOCAL_STORAGE_KEY_PREFIX } from "@/shared/utils/tutorials/constants";

const persistentKeysLS = [
  STORAGE_KEY_CONTENT_CACHE_LAST_CHECK, STORAGE_KEY_CONTENT_VERSION,
  STORAGE_KEY_IS_PWA_INSTALLED,
];
export const localStorageIgnoredSubstrings = [
  STORAGE_KEY_LAST_SC_ACTIONS, TUTORIALS_LOCAL_STORAGE_KEY_PREFIX,
];
export const localStorageIgnoredKeys = Object.values(SETTING)
  .filter((setting) => setting.saveOnLogout)
  .map((setting) => setting.id);
// Use this method to clear LS.
// By default, it preserves keys that don't need to be removed.
export const clearLocalStorage = (clearAllKeys) => {
  Object.keys(localStorageSafe).forEach((key) => {
    const removeKey = !persistentKeysLS.includes(key)
      && (
        clearAllKeys
        || (
          !localStorageIgnoredSubstrings.some((substring) => key.includes(substring))
          && !localStorageIgnoredKeys.includes(key)
        )
      );

    if (removeKey) {
      localStorageSafe.removeItem(key);
    }
  });
};

const persistentKeysSS = [STORAGE_KEY_REFRESH_COUNT];
// Use this method to clear SS.
// By default, it preserves keys that don't need to be removed.
export const clearSessionStorage = (clearAllKeys) => {
  Object.keys(sessionStorageSafe).forEach((key) => {
    const removeKey = !persistentKeysSS.includes(key) && clearAllKeys;

    if (removeKey) {
      sessionStorageSafe.removeItem(key);
    }
  });
};

export const getCookie = (name) => Cookies.get(name);
export const deleteCookie = (name) => Cookies.remove(name);

const ignoredCookies = [
  COOKIE_KEY_DONATE_NEXT_SHOW, COOKIE_KEY_DONATE_CLOSED_COUNT,
  COOKIE_KEY_THEME, STORAGE_KEY_TOKEN, _deprecated_STORAGE_KEY_REFRESH_TOKEN,
];
export const deleteAllCookies = () => {
  Object.keys(Cookies.get()).forEach((name) => {
    if (!ignoredCookies.includes(name)) {
      deleteCookie(name);
    }
  });
};

export const getTokens = () => {
  let accessToken, refreshToken, expiresTimestamp;

  const tokenCookie = getCookie(STORAGE_KEY_TOKEN, document.cookie);

  if (tokenCookie) {
    try {
      const jsonTokenCookie = JSON.parse(tokenCookie);

      accessToken = jsonTokenCookie.accessToken;
      refreshToken = jsonTokenCookie.refreshToken;
      expiresTimestamp = jsonTokenCookie.expiresTimestamp;
    } catch (e) {
      consoleLogInfo("getTokens", "Error parse token cookie: ", {
        tokenCookie,
      });
    }
  }

  if (!refreshToken) {
    const tokenLS = getLocalStorageItem(STORAGE_KEY_TOKEN, null, true);

    if (tokenLS) {
      accessToken = tokenLS.accessToken;
      refreshToken = tokenLS.refreshToken;
      expiresTimestamp = tokenLS.expiresTimestamp;
    }
  }

  // Back compatibility for production users, that are already authorized,
  // but the refresh token is saved the old way.
  if (!refreshToken) {
    refreshToken = getCookie(_deprecated_STORAGE_KEY_REFRESH_TOKEN, document.cookie);
  }
  // ========================

  // Stable no-expire token.
  const serverToken = getServerToken();
  // ================================================

  return { accessToken, expiresTimestamp, refreshToken, serverToken };
};

export const setTokens = (accessToken, refreshToken, expiresIn) => {
  if (refreshToken) {
    const token = {
      refreshToken,
      accessToken,
      expiresTimestamp: Date.now() + (expiresIn * 1000),
    };
    setCookie(STORAGE_KEY_TOKEN, JSON.stringify(token));

    // Save to the LS, for users, that has disallowed the cookies.
    setLocalStorageItem(STORAGE_KEY_TOKEN, token, true);
  } else {
    deleteCookie(STORAGE_KEY_TOKEN);
    deleteCookie(_deprecated_STORAGE_KEY_REFRESH_TOKEN);

    // Save to the LS, for users, that has disallowed the cookies.
    setLocalStorageItem(STORAGE_KEY_TOKEN, undefined);
  }
};

/**
 * return token for current session
 * @returns uuid
 */
export const getSessionToken = () => {
  let token = sessionStorageSafe.getItem("session_token");
  if (!token) {
    token = uuid();
    sessionStorageSafe.setItem("session_token", token);
  }
  return token;
};

/**
 * return token for current browser
 * @returns uuid
 */
export const getStableToken = () => {
  let token = localStorageSafe.getItem("stable_token");
  if (!token) {
    token = uuid();
    setLocalStorageItem("stable_token", token);
  }

  return token;
};
